import React from 'react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import { FallbackableImage } from '@confluence/fallbackable-image';

export const CONTENT_TYPE_ICON_MAP: Record<string, string> = {
	google_drive: 'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_document_x32.png',
	google_docs: 'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_document_x32.png',
	google_sheets: 'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_spreadsheet_x32.png',
	google_slides: 'https://ssl.gstatic.com/docs/presentations/images/favicon5.ico',
	loom: 'https://cdn.loom.com/assets/favicons-loom/favicon.ico',
	figma: 'https://static.figma.com/app/icon/1/favicon.ico',
	slack: 'https://slack.com/favicon.ico',
};

const DEFAULT_ICON_URL = '';

function normalizeContentType(type: string): string {
	return type.toLowerCase().replace(/\s+/g, '_');
}

export function getIconUrlForContentType(contentType: string): string {
	const key = normalizeContentType(contentType);
	return CONTENT_TYPE_ICON_MAP[key] ?? DEFAULT_ICON_URL;
}

const styles = cssMap({
	wrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	iconSlot: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: token('space.negative.100'),
		position: 'relative',
	},
	iconSlotFirst: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: '0',
		position: 'relative',
	},
	smallIcon: {
		width: '16px',
		height: '16px',
	},
	largeIcon: {
		width: '64px',
		height: '64px',
	},
});

const ICON_SIZE: Record<'small' | 'large', number> = {
	small: 16,
	large: 64,
};

const iconZIndex = (index: number, total: number) => total - index;

type StackedContentTypeIconsProps = {
	contentTypes: string[];
	variant?: 'small' | 'large';
	testId?: string;
};

export const StackedContentTypeIcons = ({
	contentTypes,
	variant = 'small',
	testId = 'stacked-content-type-icons',
}: StackedContentTypeIconsProps) => {
	const displayTypes = (contentTypes ?? []).slice(0, 3);
	const iconUrls = displayTypes.map((t) => getIconUrlForContentType(t));

	return (
		<Box xcss={styles.wrapper} testId={testId}>
			{iconUrls.length > 0 ? (
				iconUrls.map((url, i) => (
					<Box
						key={`${displayTypes[i]}-${i}`}
						xcss={i === 0 ? styles.iconSlotFirst : styles.iconSlot}
						style={{ zIndex: iconZIndex(i, iconUrls.length) }}
						aria-hidden="true"
					>
						<FallbackableImage
							src={url}
							width={ICON_SIZE[variant]}
							height={ICON_SIZE[variant]}
							fallback={() => <DefaultAppIcon label="" />}
							role="presentation"
						/>
					</Box>
				))
			) : (
				<DefaultAppIcon label="" color="currentColor" />
			)}
		</Box>
	);
};
